import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { axiosGet } from "../services/axiosService";
import PreviewOmni from "../components/PreviewOmni";
import environment from "../services/environment";
import { deleteCookies, getCookie, setCookie } from "../services/cookieService";
import env from "../services/environment";
import getTextColor from "@/utils/getTextColor";
import Error404 from "./Error404";

export default function EntryOmni() {
  const { url } = useParams();
  const navigate = useNavigate();
  const environmentData = env();
  const subDomain = window.location.hostname.split(".")[0];
  const [searchParams, _] = useSearchParams();
  const embed = useRef(searchParams.has("embed"));
  const [botData, setBotData] = useState<Record<string, any>>();
  const [loader, setLoader] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [livePageConfig, setLivePageConfig] = useState<Record<string, any>>({});
  const [consent, setConsent] = useState(
    getCookie("privacy_policy") ? false : true,
  );
  const [locationData, setLocationData] = useState<Record<string, any>>({
    ip: "",
    location: "unknown",
    country: "",
    region: "",
    city: "",
  });
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    function handleEnter(e: KeyboardEvent) {
      if (e.key === "Enter") setShowPreview(true);
    }
    window.addEventListener("keydown", handleEnter);
    return () => window.removeEventListener("keydown", handleEnter);
  }, []);

  useEffect(() => {
    async function init() {
      try {
        const botData = await fetchBotData();
        await fetchLocation();

        if (embed.current) {
          setShowPreview(true);
          initEmbed(botData);
        } else {
          await fetchPageConf(botData.botCustomId);
        }
      } catch (err) {
        console.log(err);
        setHasError(true);
      } finally {
        setLoader(false);
      }
    }

    init();
  }, []);

  if (hasError) {
    return <Error404 />;
  }
  async function fetchBotData() {
    if (!url) throw new Error("bot url missing");

    const params = new URLSearchParams({ url, status: "live" });
    if (environmentData.ENVIRONMENT == "production") {
      params.set("subDomain", subDomain);
    }

    const res = await axiosGet("/users/questions", { params });
    setBotData(res.data.botData);

    return res.data.botData;
  }

  async function initEmbed(botData: Record<string, any>) {
    const textColor = getTextColor(botData.chatConfig.color);

    window.parent.postMessage(
      {
        ...botData.chatConfig,
        embedConfig: botData?.embedConfig,
        close: false,
        dynamicColor: textColor,
        botCustomId: botData.botCustomId,
        SERVER_API_URL: environmentData.SERVER_API_URL,
        showGemini:
          botData?.enableGemini && botData?.embedConfig?.showVoiceIcon,
        omniData: true,
      },
      "*",
    );
  }

  async function fetchPageConf(botCustomId: string) {
    const pageConfRes = await axiosGet(
      `/liveBotPage/getPageConfiguration?botCustomId=${botCustomId}`,
    );

    setLivePageConfig(pageConfRes.data.data ?? {});
    setShowPreview(!(pageConfRes.data.data?.showPage ?? true));
  }

  async function fetchLocation() {
    const locationRes = await axiosGet("/livebot/getLocation");
    const { country, regionName, city, ip } = locationRes.data?.data;

    const locationData = {
      ip: ip?.ip_address,
      location: `${city}, ${regionName}, ${country}`,
      country: country?.toLowerCase() ?? "",
      region: regionName?.toLowerCase() ?? "",
      city: city?.toLowerCase() ?? "",
    };

    setLocationData(locationData);
  }

  if (loader) {
    return (
      <div className="pre-loader">
        <img
          src="https://res.cloudinary.com/dnag1wvx8/image/upload/v1659090825/engage/path-fillNEW_00000_ztmcqd.gif"
          alt=""
          height={140}
          width={245}
        />
      </div>
    );
  }

  if (showPreview) {
    return (
      <PreviewOmni
        botUrl={url}
        embed={embed.current}
        environment={"live"}
        botData={botData}
        consent={consent}
        setConsent={setConsent}
        locationData={locationData}
      />
    );
  }

  return (
    <div className="main-container">
      <div className="banner-body">
        <div className="banner-bd">
          <div className="banner-wrapper">
            {livePageConfig ? (
              <h2>{livePageConfig.mainHeading}</h2>
            ) : (
              <h2>
                Build your own Interactive ChatBot using
                <span>
                  {environment()?.isAI ? "Chatbot Builder" : "Omniengage"}
                </span>
              </h2>
            )}
            <p>
              {livePageConfig
                ? livePageConfig.subHeading
                : "Generate qualified and relevant leads"}
            </p>
            <button onClick={() => setShowPreview(true)}>
              {livePageConfig?.ctaText ? livePageConfig.ctaText : "Get Started"}
            </button>
          </div>
          {livePageConfig?.showBranding === false ? null : (
            <div className="branding-outer">
              <div className="branding-inner">
                Built With
                {environment()?.isAI ? (
                  <BrandingLogoCBB />
                ) : (
                  <BrandingLogoOmni />
                )}
                <span>
                  {environment()?.isAI ? "Chatbot Builder" : "OmniEngage"}{" "}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
      {botData?.cookieConsent && consent && (
        <div className={`cookieConsent`}>
          <div
            dangerouslySetInnerHTML={{
              __html: botData?.cookieConsentContent,
            }}
            className={`cookieConsent-inner`}
          />
          <div className={`btn`}>
            <button
              className={` `}
              onClick={() => {
                setConsent(false);
                setCookie("privacy_policy", true);
              }}
            >
              Accept
            </button>
            <button
              className={` `}
              onClick={() => {
                setConsent(false);
                deleteCookies("privacy_policy");
              }}
            >
              Decline
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const BrandingLogoOmni = () => (
  <svg
    width={29}
    height={28}
    viewBox="0 0 29 28"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_2085_10186)">
      <path
        d="M22.718 10.8422C23.9774 10.2899 25.4734 10.8631 25.7644 12.2072C26.2009 14.2231 26.1325 16.3294 25.5467 18.3341C24.7263 21.1414 22.9439 23.5701 20.512 25.1947C18.0802 26.8192 15.154 27.5357 12.2466 27.2186C9.33926 26.9015 6.63632 25.571 4.61181 23.4605C2.58729 21.3499 1.37036 18.594 1.17446 15.676C0.978559 12.758 1.81617 9.86416 3.54041 7.50188C5.26467 5.13965 7.7655 3.4598 10.6044 2.75699C12.6317 2.25504 14.7391 2.27424 16.7351 2.79422C18.0659 3.14088 18.5764 4.65938 17.9722 5.89478C17.368 7.13012 15.867 7.59383 14.502 7.42712C13.606 7.31773 12.6908 7.37075 11.8012 7.591C10.0979 8.01272 8.59739 9.02061 7.56286 10.438C6.52828 11.8553 6.02575 13.5916 6.14327 15.3424C6.26079 17.0932 6.99094 18.7468 8.20566 20.0131C9.42037 21.2794 11.0421 22.0777 12.7865 22.268C14.531 22.4583 16.2867 22.0283 17.7458 21.0536C19.2049 20.0789 20.2744 18.6216 20.7665 16.9373C21.0236 16.0577 21.1147 15.1455 21.0426 14.2457C20.9328 12.8749 21.4586 11.3944 22.718 10.8422Z"
        fill="#FB5F66"
      />
      <path
        d="M27.2799 6.87081C26.8448 7.21147 25.5525 6.72189 24.1552 6.07874C23.0413 6.50435 22.321 6.26301 23.8277 7.88542C24.9258 9.06768 21.7416 8.92601 19.9035 10.7641C17.5372 13.2177 18.5705 18.7253 15.6631 14.1324C14.5676 12.4018 16.1237 17.6853 15.3855 17.9002C14.4066 18.065 14.5265 16.8718 14.1757 14.0777C14.0084 12.7458 13.8104 13.9541 13.5726 15.4165C14.59 17.8493 14.2542 16.2574 13.5286 17.139L13.3172 16.8999C13.1407 17.8116 12.9453 18.494 12.7283 18.2771C12.2273 17.7759 11.721 15.1833 11.0408 14.3259L10.9492 14.2224C10.9801 14.2533 11.0106 14.2879 11.0408 14.3259L13.3172 16.8999C13.4072 16.4354 13.4922 15.9114 13.5726 15.4165C13.2627 14.6752 12.827 13.5602 12.2385 11.8877C11.6122 9.40722 15.8552 10.8004 17.5817 9.42265C19.3083 8.04494 19.9668 6.25264 19.6963 4.42994C19.7303 3.80576 22.0608 5.11479 24.1552 6.07874C24.5117 5.94249 24.9085 5.73797 25.2856 5.38205C25.5541 5.12852 25.7666 4.85554 25.9041 4.56289L23.5358 0.590332C25.914 2.17858 26.4033 3.50021 25.9041 4.56289L27.2799 6.87081Z"
        fill="#444444"
      />
      <path
        d="M26.8215 1.85207C25.0928 0.123352 22.29 0.123353 20.5613 1.85207C18.8326 3.58079 18.8326 6.38359 20.5613 8.11231C22.29 9.84103 25.0928 9.84103 26.8215 8.11231C28.5502 6.38359 28.5502 3.58079 26.8215 1.85207Z"
        fill="#444444"
      />
      <path
        d="M17.51 11.1642C15.4356 9.08974 12.0722 9.08974 9.99776 11.1642C7.9233 13.2387 7.9233 16.602 9.99776 18.6765C12.0722 20.7509 15.4356 20.7509 17.51 18.6765C19.5845 16.602 19.5845 13.2387 17.51 11.1642Z"
        fill="#444444"
      />
    </g>
    <defs>
      <clipPath id="clip0_2085_10186">
        <rect
          width={27.6862}
          height={27.6862}
          fill="white"
          transform="translate(0.701172)"
        />
      </clipPath>
    </defs>
  </svg>
);

const BrandingLogoCBB = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M11.2374 7.32417L11.2362 7.34163V7.35914C11.2362 7.89245 11.1281 8.22884 10.9809 8.44996C10.8369 8.6663 10.6214 8.82361 10.3049 8.93664C9.97956 9.05282 9.56787 9.11458 9.06559 9.14387C8.62435 9.1696 8.14337 9.16916 7.61923 9.16868C7.54762 9.16862 7.47522 9.16855 7.40199 9.16855C7.32877 9.16855 7.25636 9.16862 7.18476 9.16868C6.66062 9.16916 6.17964 9.1696 5.7384 9.14387C5.23612 9.11458 4.82442 9.05282 4.49909 8.93664C4.18261 8.82361 3.96708 8.6663 3.82306 8.44996C3.67585 8.22884 3.56776 7.89245 3.56776 7.35914C3.56776 5.2349 5.291 3.48182 7.40199 3.48182C9.55907 3.48182 11.3861 5.13159 11.2374 7.32417Z"
      stroke="#444444"
      strokeWidth="1.03395"
    />
    <path
      d="M2.75549 3.99891C-0.0153649 8.08562 2.67627 11.0943 4.85986 10.9699C4.91074 10.967 4.95452 11.0067 4.95452 11.0576V12.9632C4.95452 13.0406 5.04861 13.0787 5.10251 13.0232L7.04553 11.0213C7.08165 10.9841 7.13302 10.9652 7.18471 10.9695C9.88113 11.1931 12.0041 10.6999 12.7894 9.15648C13.7737 7.22207 12.8324 4.84835 11.8847 3.81439C9.9029 1.05719 4.95449 0.755614 2.75549 3.99891Z"
      stroke="url(#paint0_linear_2541_12675)"
      strokeWidth="1.20628"
    />
    <ellipse
      cx="5.81734"
      cy="6.6689"
      rx="0.540974"
      ry="0.540973"
      fill="#FB5F66"
    />
    <ellipse
      cx="7.44039"
      cy="6.6689"
      rx="0.540974"
      ry="0.540973"
      fill="#FB5F66"
    />
    <ellipse
      cx="9.06343"
      cy="6.6689"
      rx="0.540974"
      ry="0.540973"
      fill="#FB5F66"
    />
    <defs>
      <linearGradient
        id="paint0_linear_2541_12675"
        x1="3.35457"
        y1="1.91882"
        x2="7.26257"
        y2="13.1757"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#FF898E" />
        <stop offset="0.546875" stopColor="#EA0D17" />
        <stop offset="1" stopColor="#FB5F66" />
      </linearGradient>
    </defs>
  </svg>
);
